/**
 * Il listino come lo mostra la pagina prezzi.
 *
 * La fonte e' il backend (`GET /api/abbonamenti/piani`, generato da
 * piani.py): questa copia statica serve solo a far comparire la sezione
 * prezzi anche quando il backend non risponde, e va tenuta uguale.
 *
 * `per` e' a chi si rivolge il piano: la riga sotto il prezzo nella scheda.
 * Le utenze stanno fra le voci. Le note fiscali (`prezzo.nota`, importo
 * minimo) la pagina le mostra una volta sola, sotto il listino.
 */

export const LISTINO_STATICO = [
  {
    chiave: "web", nome: "Web", abbonabile: false, utenti: null,
    prezzo: { tipo: "a_consumo", per_pagina_eur: 0.1, minimo_eur: 1, nota: "IVA inclusa, senza account" },
    // (tutti i prezzi del listino sono IVA inclusa)
    per: "Per chi anonimizza ogni tanto, senza account",
    pagine: "Paghi le pagine che elabori",
    voci: [
      { testo: "Anonimizzazione automatica di dati personali e riservati", disponibile: true },
      { testo: "Performance eccellenti su testi e documenti italiani", disponibile: true },
      { testo: "Sfocatura dei volti", disponibile: true },
      { testo: "Tutti i formati: PDF, DOCX, immagini, CSV, PPTX", disponibile: true },
      { testo: "Conforme al GDPR, NIS2 e AI Act", disponibile: true },
      { testo: "Supporto via email", disponibile: true },
    ],
  },
  {
    chiave: "silver", nome: "Silver", abbonabile: true, utenti: 3,
    prezzo: { tipo: "abbonamento", mensile_eur: 59, annuale_eur: 590, nota: "IVA inclusa" },
    per: "Per il professionista singolo o il piccolo studio",
    pagine: "750 pagine al mese incluse",
    voci: [
      { testo: "Tutto il piano Web", disponibile: true },
      { testo: "Fino a 3 utenze", disponibile: true },
      { testo: "Anonimizzazione leggibile: [PERSONA_1] al posto del nome", disponibile: true },
      { testo: "Regole di anonimizzazione personalizzate", disponibile: false },
      { testo: "Report di conformità", disponibile: false },
      { testo: "0,08 € per ogni pagina aggiuntiva", disponibile: true },
    ],
  },
  {
    chiave: "gold", nome: "Gold", abbonabile: true, utenti: 10,
    prezzo: { tipo: "abbonamento", mensile_eur: 499, annuale_eur: 4990, nota: "IVA inclusa" },
    per: "Per studi e aziende con un team fino a 10 persone",
    pagine: "Pagine illimitate",
    voci: [
      { testo: "Tutto il piano Silver", disponibile: true },
      { testo: "Fino a 10 utenze", disponibile: true },
      { testo: "Sfocatura delle firme", disponibile: true },
      { testo: "API con chiavi e documentazione", disponibile: false },
    ],
  },
  {
    chiave: "platinum", nome: "Platinum", abbonabile: true, utenti: 25,
    prezzo: { tipo: "abbonamento", mensile_eur: 999, annuale_eur: 9990, nota: "IVA inclusa" },
    per: "Per aziende strutturate con team fino a 25 persone",
    pagine: "Pagine illimitate",
    voci: [
      { testo: "Tutto il piano Gold", disponibile: true },
      { testo: "Fino a 25 utenze", disponibile: true },
      { testo: "Audit trail", disponibile: false },
      { testo: "Supporto con referente dedicato", disponibile: true },
    ],
  },
  {
    chiave: "enterprise", nome: "Enterprise", abbonabile: false, utenti: null,
    prezzo: { tipo: "preventivo", nota: "Su misura" },
    per: "Per aziende con esigenze specifiche",
    pagine: "Su misura",
    voci: [
      { testo: "Installazione on-premise", disponibile: true },
      { testo: "SSO", disponibile: true },
      { testo: "Modelli addestrati sui tuoi documenti", disponibile: true },
      { testo: "API e connettori personalizzati", disponibile: true },
      { testo: "Account manager e SLA", disponibile: true },
      { testo: "Supporto operativo dedicato", disponibile: true },
    ],
  },
];

/** La riga fiscale che la pagina mostra una volta sola, sotto il listino. */
export const NOTA_LISTINO = "Tutti i prezzi sono IVA inclusa. Il pagamento a consumo ha un importo minimo di 1 € per transazione.";

const euroIntero = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const euroDecimale = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

export function formattaEuro(n) {
  return Number.isInteger(n) ? euroIntero.format(n) : euroDecimale.format(n);
}

/**
 * Importo e unita' da mostrare in testa alla scheda.
 * @param {object} piano voce del listino
 * @param {"mensile"|"annuale"} cadenza
 */
export function prezzoPerCadenza(piano, cadenza) {
  const p = piano?.prezzo || {};
  if (p.tipo === "a_consumo") {
    return { importo: formattaEuro(p.per_pagina_eur), unita: "/ pagina", nota: p.nota, dettaglio: `importo minimo per transazione ${formattaEuro(p.minimo_eur)}`, risparmio: null };
  }
  if (p.tipo === "abbonamento") {
    const annuale = cadenza === "annuale";
    const importo = annuale ? p.annuale_eur : p.mensile_eur;
    const mesiGratis = annuale && p.mensile_eur ? Math.round(12 - p.annuale_eur / p.mensile_eur) : 0;
    return {
      importo: formattaEuro(importo),
      unita: annuale ? "/ anno" : "/ mese",
      nota: p.nota,
      dettaglio: annuale ? "fatturati una volta l'anno" : "fatturati ogni mese",
      // Il vantaggio dell'annuale, da mostrare come etichetta accanto al prezzo.
      risparmio: annuale && mesiGratis > 0 ? `${mesiGratis} mesi gratis` : null,
    };
  }
  return { importo: "Su misura", unita: "", nota: p.nota, dettaglio: "Contattaci per un preventivo", risparmio: null };
}

/** Piani a cui ci si puo' abbonare, nell'ordine del listino. */
export function pianiAbbonabili(listino) {
  return (listino || LISTINO_STATICO).filter((p) => p.abbonabile);
}
